import { useState, Component } from 'react';
import { withTranslation, Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { APP_NAME } from '../../../utils/constant';
import { LocaleService } from '../../../services';

import {
  getAuditUniverseBreakdown,
  getAuditSingleStatusCounts,
  getAuditInspectUnresolved,
  getAuditInspectOrphanSingles,
} from '../../../store/actions';

import './audit.scss';

const fmt = (n) =>
  n === null || n === undefined ? '—' : LocaleService.fmtLocaleNumeral(n);

const pct = (part, total) =>
  !total ? '—' : `${Math.round((part / total) * 100)}%`;

// ── Sub-components ────────────────────────────────────────────────────────────

const LoadingSpinner = ({ t }) => (
  <div className="audit-loading">
    <div
      className="spinner-border spinner-border-sm text-secondary"
      role="status"
    />
    <span>{t('common.loading')}…</span>
  </div>
);

const FunnelTable = ({ t, universe }) => {
  if (!universe) return null;
  const { type_breakdown: bd, breakdown_detail: detail } = universe;
  const rows = [
    { label: t('audit.tables.headings.universe'), ...bd.universe },
    { label: t('audit.tables.headings.valid'), ...bd.valid },
    { label: t('audit.tables.headings.reportable'), ...bd.reportable },
  ];

  return (
    <div className="audit-card">
      <h5 className="audit-card__title">
        {t('audit.activity_universe_funnel')}
      </h5>
      <table className="audit-table">
        <thead>
          <tr>
            <th>{t('audit.tables.headings.tier')}</th>
            <th className="num">{t('common.total')}</th>
            <th className="num">{t('audit.tables.headings.singles')}</th>
            <th className="num">{t('audit.tables.headings.programs')}</th>
            <th className="num">
              {t('audit.tables.headings.pct_of_universe')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ label, total, singles, programs }) => (
            <tr
              key={label}
              className={label === 'Reportable' ? 'row--highlight' : ''}
            >
              <td>{label}</td>
              <td className="num">{fmt(total)}</td>
              <td className="num">{fmt(singles)}</td>
              <td className="num">{fmt(programs)}</td>
              <td className="num">{pct(total, bd.universe.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="audit-note">
        <Trans
          i18nKey="audit.deleted_excluded_from_universe"
          values={{ numDeleted: fmt(detail?.deleted) }}
        />
      </p>
    </div>
  );
};

const SingleStatusTable = ({ t, singleStatus }) => {
  if (!singleStatus) return null;
  const { status_buckets: buckets, total } = singleStatus;

  const STATUS_LABELS = {
    Unapproved: 'Development (Unapproved)',
    Approved: 'Active (Approved)',
    Archived: 'Completed (Archived)',
  };

  const STATUS_ORDER = ['Unapproved', 'Approved', 'Archived'];
  const sortedBuckets = [...buckets].sort(
    (a, b) =>
      STATUS_ORDER.indexOf(a.status_name) - STATUS_ORDER.indexOf(b.status_name)
  );

  return (
    <div className="audit-card">
      <h5 className="audit-card__title">
        {t('audit.reportable_singles_by_status')}
      </h5>
      <table className="audit-table">
        <thead>
          <tr>
            <th>{t('formulary.status')}</th>
            <th className="num">{t('report.count')}</th>
            <th className="num">{t('audit.percentage_of_total')}</th>
          </tr>
        </thead>
        <tbody>
          {sortedBuckets.map(({ status_name, n }) => (
            <tr key={status_name}>
              <td>{STATUS_LABELS[status_name] || status_name}</td>
              <td className="num">{fmt(n)}</td>
              <td className="num">{pct(Number(n), total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>
              <strong>{t('common.total')}</strong>
            </td>
            <td className="num">
              <strong>{fmt(total)}</strong>
            </td>
            <td className="num" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const InspectSection = ({
  t,
  title,
  note,
  loading,
  data,
  onOpen,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const count = data?.count ?? 0;

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && onOpen) onOpen();
  };

  return (
    <div className="audit-card">
      <div
        className="audit-card__title audit-card__title--toggle"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleToggle()}
      >
        <span>{title}</span>
        <span
          className={`audit-badge ${
            count > 0 ? 'audit-badge--warn' : 'audit-badge--ok'
          }`}
        >
          {loading ? '…' : fmt(count)}
        </span>
        <span className="audit-chevron">{open ? '▲' : '▼'}</span>
      </div>
      {note && <p className="audit-note">{note}</p>}
      {open &&
        (loading ? (
          <LoadingSpinner t={t} />
        ) : count === 0 ? (
          <p className="audit-empty">{t('audit.no_issues_found')}</p>
        ) : (
          children
        ))}
    </div>
  );
};

const UnresolvedTable = ({ t, rows }) => (
  <div className="audit-table-wrap">
    <table className="audit-table audit-table--inspect">
      <thead>
        <tr>
          <th>{t('audit.tables.headings.id')}</th>
          <th>{t('audit.tables.headings.name')}</th>
          <th>{t('audit.tables.headings.type')}</th>
          <th>{t('audit.tables.headings.status')}</th>
          <th>{t('audit.tables.headings.owner_email')}</th>
          <th>{t('audit.tables.headings.direct_org')}</th>
          <th>{t('audit.tables.headings.parent_program')}</th>
          <th>{t('audit.tables.headings.parent_org')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{r.id}</td>
            <td>{r.name}</td>
            <td>{r.planType}</td>
            <td>{r.status || '—'}</td>
            <td>{r.ownerEmail || '—'}</td>
            <td>
              {r.directOrgName ? `${r.directOrgName} (${r.directOrgId})` : '—'}
            </td>
            <td>{r.parentProjectName || '—'}</td>
            <td>{r.parentOrgName || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const OrphanSinglesTable = ({ t, rows }) => (
  <div className="audit-table-wrap">
    <table className="audit-table audit-table--inspect">
      <thead>
        <tr>
          <th>{t('audit.tables.headings.id')}</th>
          <th>{t('audit.tables.headings.name')}</th>
          <th>{t('audit.tables.headings.status')}</th>
          <th>{t('audit.tables.headings.owner_email')}</th>
          <th>{t('audit.tables.headings.org')}</th>
          <th>{t('audit.tables.headings.demo')}</th>
          <th>{t('audit.tables.headings.parent_id')}</th>
          <th>{t('audit.tables.headings.parent_name')}</th>
          <th>{t('audit.tables.headings.parent_plan_type')}</th>
          <th>{t('audit.tables.headings.reason')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{r.id}</td>
            <td>{r.name}</td>
            <td>{r.status || '—'}</td>
            <td>{r.ownerEmail || '—'}</td>
            <td>{r.orgName || '—'}</td>
            <td>{r.isDemo ? t('actions.yes') : t('actions.no')}</td>
            <td>{r.parentId || '—'}</td>
            <td>{r.parentProjectName || '—'}</td>
            <td>{r.parentPlanTypeId || '—'}</td>
            <td>
              <span className="audit-reason">{r.orphanReason}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Main container ────────────────────────────────────────────────────────────

class AuditPage extends Component {
  componentDidMount() {
    const { t } = this.props;
    document.title = `${t('audit.data_audit')} | ${APP_NAME}`;
    const { getAuditUniverseBreakdown, getAuditSingleStatusCounts } =
      this.props;
    // Summary panels load immediately; inspection panels load on demand when opened
    getAuditUniverseBreakdown();
    getAuditSingleStatusCounts();
  }

  handleRefresh = () => {
    const {
      getAuditUniverseBreakdown,
      getAuditSingleStatusCounts,
      getAuditInspectUnresolved,
      getAuditInspectOrphanSingles,
      unresolved,
      orphanSingles,
    } = this.props;
    // Always refresh summary panels
    getAuditUniverseBreakdown();
    getAuditSingleStatusCounts();
    // Refresh inspection panels only if they were already loaded
    if (unresolved !== null) getAuditInspectUnresolved();
    if (orphanSingles !== null) getAuditInspectOrphanSingles();
  };

  render() {
    const {
      t,
      universeLoading,
      universe,
      singleStatusLoading,
      singleStatus,
      unresolvedLoading,
      unresolved,
      orphanSinglesLoading,
      orphanSingles,
      getAuditInspectUnresolved,
      getAuditInspectOrphanSingles,
    } = this.props;

    const isLoading =
      universeLoading ||
      singleStatusLoading ||
      unresolvedLoading ||
      orphanSinglesLoading;

    return (
      <div className="audit-page">
        <div className="audit-page__header">
          <div>
            <h4 className="audit-page__heading">{t('audit.data_audit')}</h4>
            <p className="audit-page__sub">
              {t('audit.internal_data_quality_checks')}
            </p>
          </div>
          <button
            className="audit-refresh-btn"
            onClick={this.handleRefresh}
            disabled={isLoading}
            title={t('formulary.reload_all_data')}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />{' '}
                {t('actions.refreshing')}…
              </>
            ) : (
              `↻ ${t('actions.refresh')}`
            )}
          </button>
        </div>

        {/* Universe funnel */}
        {universeLoading && !universe ? (
          <LoadingSpinner t={t} />
        ) : (
          <FunnelTable t={t} universe={universe} />
        )}

        {/* Singles by status */}
        {singleStatusLoading && !singleStatus ? (
          <LoadingSpinner t={t} />
        ) : (
          <SingleStatusTable t={t} singleStatus={singleStatus} />
        )}

        {/* Inspection: Unresolved */}
        <InspectSection
          title={t('audit.activities_with_no_resolvable_org')}
          note={t('audit.non_deleted_activities_parent_not_resolved')}
          loading={unresolvedLoading}
          data={unresolved}
          onOpen={getAuditInspectUnresolved}
          {...this.props}
        >
          <UnresolvedTable t={t} rows={unresolved?.rows ?? []} />
        </InspectSection>

        {/* Inspection: Orphan singles */}
        <InspectSection
          title={t('audit.linked_singles_with_no_valid_parent')}
          note={t('audit.singles_flagged_as_linked')}
          loading={orphanSinglesLoading}
          data={orphanSingles}
          onOpen={getAuditInspectOrphanSingles}
          {...this.props}
        >
          <OrphanSinglesTable t={t} rows={orphanSingles?.rows ?? []} />
        </InspectSection>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  universeLoading: state.audit.universeLoading,
  universe: state.audit.universe,
  singleStatusLoading: state.audit.singleStatusLoading,
  singleStatus: state.audit.singleStatus,
  unresolvedLoading: state.audit.unresolvedLoading,
  unresolved: state.audit.unresolved,
  orphanSinglesLoading: state.audit.orphanSinglesLoading,
  orphanSingles: state.audit.orphanSingles,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getAuditUniverseBreakdown,
      getAuditSingleStatusCounts,
      getAuditInspectUnresolved,
      getAuditInspectOrphanSingles,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(AuditPage));
